const FULL_CIRCLE_DEGREES = 360;

export const PORTAL_SPATIAL_MODEL_VERSION = "portal-spatial-model.v2";

export const WORLDX_SECTORS = Object.freeze([
  { label: "Ascend Axis", captureHeadingDegrees: -1.33, captureElevationDegrees: 0, calibrationFrame: "desktop-worldx-horizon-00.png", meaning: "The primary progression path: convergence, disciplined growth, visible ascent, and transformation." },
  { label: "World-Class", captureHeadingDegrees: 45.4, captureElevationDegrees: 0, calibrationFrame: "desktop-worldx-horizon-02.png", meaning: "International proof represented through Singapore and global-quality positioning." },
  { label: "Scale MAXX", captureHeadingDegrees: 45.4, captureElevationDegrees: 18, calibrationFrame: "desktop-worldx-horizon-02.png", meaning: "Turning creative capability into scalable influence, systems, and reach." },
  { label: "Aura Engineering", captureHeadingDegrees: 90.41, captureElevationDegrees: 0, calibrationFrame: "desktop-worldx-horizon-04.png", meaning: "Intentionally constructing atmosphere through colour, light, energy, and sensory composition." },
  { label: "MAXX Experience", captureHeadingDegrees: 135.45, captureElevationDegrees: 0, calibrationFrame: "desktop-worldx-horizon-06.png", meaning: "Coordinating design, emotion, identity, and usefulness into one experience system." },
  { label: "Mood · Style · Taste · Signature", captureHeadingDegrees: 135.45, captureElevationDegrees: 18, calibrationFrame: "desktop-worldx-horizon-06.png", meaning: "The qualities that make an experience emotionally distinct and recognisably Joz." },
  { label: "Maximize Beauty · Change · Reality", captureHeadingDegrees: 170, captureElevationDegrees: 0, calibrationFrame: "desktop-worldx-horizon-07.png", meaning: "Using beauty to shift energy, encourage action, and alter perceived reality." },
  { label: "AI Synthesis", captureHeadingDegrees: -136.83, captureElevationDegrees: 0, calibrationFrame: "desktop-worldx-horizon-10.png", meaning: "Connecting and recombining information to create new relationships and possibilities." },
  { label: "AI Analysis", captureHeadingDegrees: -136.83, captureElevationDegrees: -8, calibrationFrame: "desktop-worldx-horizon-10.png", meaning: "Decomposing and structuring information to produce understanding." },
  { label: "Atmos MAXX", captureHeadingDegrees: -89.6, captureElevationDegrees: 0, calibrationFrame: "desktop-worldx-horizon-12.png", meaning: "Intensifying light, space, sound, motion, mood, and presence until the environment changes perception." },
  { label: "Alpha PSL", captureHeadingDegrees: -44.67, captureElevationDegrees: 0, calibrationFrame: "desktop-worldx-horizon-14.png", meaning: "Dubai-based regional proof of execution, prestige, and creative capability." },
  { label: "Clout MAXX", captureHeadingDegrees: -44.67, captureElevationDegrees: 18, calibrationFrame: "desktop-worldx-horizon-14.png", meaning: "Visible recognition, influence, and cultural impact." },
]);

export const PORTAL_SPATIAL_MODELS = Object.freeze({
  root: {
    id: "root",
    title: "Moon / Exocortex",
    horizontalSectors: 8,
    lowerHemisphereExposed: false,
    sources: [
      "desktop-portal-pixel-census-2026-08-12/contact-sheets/root-360.png",
      "desktop-portal-pixel-census-2026-08-12/rotation/rotation-manifest.json",
      "data/meetjoz/world/spatial-coverage.json#root_360_coverage",
    ],
    sectorTargets: [
      ["root_enter", "root_brain"],
      ["root_enter", "root_brain"],
      ["root_sector_right"],
      ["root_ascend", "root_aura"],
      ["root_ascend", "root_aura"],
      ["root_ascend", "root_aura"],
      ["root_sector_left"],
      ["root_enter", "root_brain"],
    ],
    assets: {
      root_enter: "enter.glb",
      root_brain: "bx.glb",
      root_ascend: "ascend.glb",
      root_aura: "aura.glb",
      root_gold_pill: "ball.glb",
      root_sector_left: null,
      root_sector_right: null,
      root_sector_lower: null,
    },
    bearings: {
      root_enter: 0,
      root_brain: 0,
      root_ascend: 180,
      root_aura: 180,
      root_sector_right: 90,
      root_sector_left: 270,
    },
  },
  maxx: {
    id: "maxx",
    title: "neoMAXX synaptic world",
    horizontalSectors: 8,
    lowerHemisphereExposed: false,
    sources: [
      "desktop-portal-pixel-census-2026-08-12/contact-sheets/neomaxx-active-360.png",
      "desktop-portal-pixel-census-2026-08-12/contact-sheets/neomaxx-paused-360.png",
      "data/meetjoz/world/spatial-coverage.json#maxx_360_coverage",
    ],
    sectorTargets: Array.from({ length: 8 }, () => ["maxx_neurons", "maxx_receptors"]),
    pausedTargets: ["maxx_neurodesign", "maxx_neurons"],
    assets: {
      maxx_neurons: "n2x.glb",
      maxx_receptors: "n3.glb",
      maxx_neurodesign: "neurodesign plane",
    },
  },
  meet_joz: {
    id: "meet_joz",
    title: "Meet Joz identity and proof world",
    horizontalSectors: 8,
    lowerHemisphereExposed: false,
    sources: [
      "desktop-portal-pixel-census-2026-08-12/contact-sheets/meet-joz-flex-360.png",
      "desktop-portal-pixel-census-2026-08-12/contact-sheets/meet-joz-ascend-360.png",
      "desktop-portal-pixel-census-2026-08-12/contact-sheets/meet-joz-mogg-360.png",
      "desktop-portal-pixel-census-2026-08-12/contact-sheets/meet-joz-workf-360.png",
      "desktop-portal-pixel-census-2026-08-12/contact-sheets/meet-joz-workf-detail-360.png",
      "data/meetjoz/world/spatial-coverage.json#meet_joz_360_coverage",
    ],
    stageTargets: {
      flex: ["meet_joz_flex"],
      ascend: ["meet_joz_ascend", "meet_joz_ascend_particles", "meet_joz_semantic_city"],
      mogg: ["meet_joz_mogg"],
      workf: ["meet_joz_mogg", "meet_joz_skills"],
      detail: ["meet_joz_skills_panel", "meet_joz_skills"],
    },
    assets: {
      meet_joz_flex: "jkx.glb",
      meet_joz_ascend: "ascend field",
      meet_joz_ascend_particles: "ascend particles",
      meet_joz_semantic_city: "worldx.glb",
      meet_joz_mogg: "mogg digital twin",
      meet_joz_skills: "workf proof field",
      meet_joz_skills_panel: "workf detail plane",
    },
  },
});

export function normalizeHeadingDegrees(value) {
  const degrees = Number(value);
  if (!Number.isFinite(degrees)) return 0;
  return ((degrees % FULL_CIRCLE_DEGREES) + FULL_CIRCLE_DEGREES) % FULL_CIRCLE_DEGREES;
}

export function worldxSectorFromCenterDirection(direction) {
  const x = Number(direction?.x ?? direction?.[0]);
  const y = Number(direction?.y ?? direction?.[1] ?? 0);
  const z = Number(direction?.z ?? direction?.[2]);
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z) || Math.hypot(x, y, z) < 1e-6) return null;
  const headingDegrees = normalizeHeadingDegrees((Math.atan2(x, -z) * 180) / Math.PI);
  const pitchDegrees = (Math.asin(Math.max(-1, Math.min(1, y / Math.hypot(x, y, z)))) * 180) / Math.PI;
  return worldxLandmarkFromView({ headingDegrees, pitchDegrees });
}

export function worldxSectorFromHeadingDegrees(value) {
  return worldxLandmarkFromView({ headingDegrees: value, pitchDegrees: 0 });
}

export function worldxLandmarkFromView({ headingDegrees: value, pitchDegrees: pitchValue = 0 } = {}) {
  const headingDegrees = normalizeHeadingDegrees(value);
  const pitchDegrees = Number.isFinite(Number(pitchValue)) ? Number(pitchValue) : 0;
  let sectorIndex = 0;
  let smallestDistance = Infinity;
  let headingDeltaDegrees = Infinity;
  let elevationDeltaDegrees = Infinity;
  WORLDX_SECTORS.forEach((sector, index) => {
    const headingDelta = Math.abs(signedHeadingDeltaDegrees(sector.captureHeadingDegrees, headingDegrees));
    const elevationDelta = Math.abs((sector.captureElevationDegrees || 0) - pitchDegrees);
    const angularDistance = Math.hypot(headingDelta, elevationDelta);
    if (angularDistance < smallestDistance) {
      smallestDistance = angularDistance;
      headingDeltaDegrees = headingDelta;
      elevationDeltaDegrees = elevationDelta;
      sectorIndex = index;
    }
  });
  return {
    sectorIndex,
    headingDegrees: Number(headingDegrees.toFixed(1)),
    pitchDegrees: Number(pitchDegrees.toFixed(1)),
    label: WORLDX_SECTORS[sectorIndex].label,
    meaning: WORLDX_SECTORS[sectorIndex].meaning,
    calibrationDeltaDegrees: Number(smallestDistance.toFixed(2)),
    headingDeltaDegrees: Number(headingDeltaDegrees.toFixed(2)),
    elevationDeltaDegrees: Number(elevationDeltaDegrees.toFixed(2)),
  };
}

export function signedHeadingDeltaDegrees(target, heading) {
  const delta = normalizeHeadingDegrees(target) - normalizeHeadingDegrees(heading);
  return ((delta + 540) % FULL_CIRCLE_DEGREES) - 180;
}

function portalId(value) {
  const id = String(value || "root").toLowerCase().replace(/-/g, "_");
  if (["maxx", "neo_maxx", "neomaxx", "the_vibe_energy"].includes(id)) return "maxx";
  if (["meet_joz", "meetjoz"].includes(id)) return "meet_joz";
  return "root";
}

function meetJozStage(stage, mesh) {
  const state = `${stage || ""} ${mesh || ""}`.toLowerCase();
  if (/detail|panel|workf.*detail/.test(state)) return "detail";
  if (/workf|skills/.test(state)) return "workf";
  if (/mogg/.test(state)) return "mogg";
  if (/ascend|discover|worldx/.test(state)) return "ascend";
  return "flex";
}

function maxxTargets(model, stage, mesh) {
  const state = `${stage || ""} ${mesh || ""}`.toLowerCase();
  return /pause|neurodesign|elite|inside_the_brain/.test(state) ? model.pausedTargets : null;
}

function elevationBand(pitchDegrees) {
  const pitch = Number(pitchDegrees) || 0;
  if (pitch >= 65) return "overhead";
  if (pitch >= 18) return "upward";
  if (pitch <= -18) return "lower";
  return "horizon";
}

export function resolvePortalSpatialView({
  portal = "root",
  stage = null,
  mesh = null,
  yawDegrees = 0,
  pitchDegrees = 0,
} = {}) {
  const id = portalId(portal);
  const model = PORTAL_SPATIAL_MODELS[id];
  const headingDegrees = normalizeHeadingDegrees(yawDegrees);
  const sectorSize = FULL_CIRCLE_DEGREES / model.horizontalSectors;
  const sectorIndex = Math.round(headingDegrees / sectorSize) % model.horizontalSectors;
  let targetIds;
  let stageId = null;

  if (id === "meet_joz") {
    stageId = meetJozStage(stage, mesh);
    targetIds = model.stageTargets[stageId];
  } else if (id === "maxx") {
    targetIds = maxxTargets(model, stage, mesh) || model.sectorTargets[sectorIndex];
  } else {
    targetIds = model.sectorTargets[sectorIndex];
    if (Number(pitchDegrees) <= -18) targetIds = ["root_sector_lower"];
  }

  const sectorCenterDegrees = sectorIndex * sectorSize;
  const elevationDegrees = Number((-(Number(pitchDegrees) || 0)).toFixed(1));
  const candidates = targetIds.map((targetId, index) => {
    const targetBearingDegrees = model.bearings?.[targetId];
    const bearingDegrees = Number((Number.isFinite(targetBearingDegrees)
      ? signedHeadingDeltaDegrees(targetBearingDegrees, headingDegrees)
      : 0
    ).toFixed(1));
    const targetElevationDegrees = targetId === "root_sector_lower" ? 0 : elevationDegrees;
    return {
      id: targetId,
      asset: model.assets[targetId] || null,
      bearingDegrees,
      elevationDegrees: targetElevationDegrees,
      angularDistanceDegrees: Number(Math.hypot(bearingDegrees, targetElevationDegrees).toFixed(1)),
      priority: index,
    };
  });

  const worldxSector = id === "meet_joz" && stageId === "ascend"
    ? worldxLandmarkFromView({ headingDegrees, pitchDegrees })
    : null;

  return {
    method: "portal-spatial-model",
    modelVersion: PORTAL_SPATIAL_MODEL_VERSION,
    portalId: id,
    stageId,
    headingDegrees: Number(headingDegrees.toFixed(1)),
    pitchDegrees: Number((Number(pitchDegrees) || 0).toFixed(1)),
    sectorIndex,
    sectorId: `${id}:sector-${String(sectorIndex).padStart(2, "0")}`,
    sectorCenterDegrees,
    elevationBand: elevationBand(pitchDegrees),
    primaryObjectId: candidates[0]?.id || null,
    candidates,
    worldxSector: worldxSector === null ? null : {
      index: worldxSector.sectorIndex,
      label: worldxSector.label,
      meaning: worldxSector.meaning,
      calibrationDeltaDegrees: worldxSector.calibrationDeltaDegrees,
    },
    confidence: "verified-map",
    provenance: model.sources,
  };
}
